/** ALINIEREA UNUI OBIECT LA VECINII LUI — snap-ul de poziție și cel de unghi.
 *
 *  CE REZOLVĂ. Mutat cu mâna, un dulap se oprește la doi-trei centimetri de
 *  peretele pe care ar trebui să stea, iar două noptiere puse de-o parte și de
 *  alta a patului nu ies niciodată pe aceeași linie. La 60 px/m, un centimetru
 *  e jumătate de pixel: nu e o problemă de atenție, e una de rezoluție.
 *
 *  ────────────────────────────────────────────────────────────────────────────
 *  SE ALINIAZĂ MUCHII CU MUCHII. ATÂT.
 *
 *  Regula e îngustă anume, și e cerută pe nume: «în niciun caz nu propune
 *  snap-uri cu intersecții sau în care aliniem centrul unui obiect cu marginea
 *  altui obiect; snap-ul trebuie făcut în funcție de marginile ambelor
 *  obiecte».
 *
 *  Deci candidații se scriu dintr-o MUCHIE a obiectului mutat și o MUCHIE a
 *  unui vecin, niciodată din centre, axe sau puncte de intersecție. Pe o axă,
 *  un obiect are două muchii și vecinul două, deci ies patru potriviri — și
 *  toate patru au un înțeles pe planșă:
 *
 *    * stânga cu stânga, dreapta cu dreapta — obiectele stau ALINIATE, cu
 *      fețele pe aceeași linie (două noptiere, un rând de dulapuri);
 *    * stânga cu dreapta — obiectele stau LIPITE, unul în continuarea
 *      celuilalt, fără rost între ele (dulapul de peretele de lângă).
 *
 *  Un centru aliniat cu o muchie nu e nici una, nici alta: e o coincidență pe
 *  care n-o vede nimeni pe planșă și pe care n-o cere nimeni. Nu se propune.
 *
 *  ────────────────────────────────────────────────────────────────────────────
 *  TREI OPRELIȘTI, ca lipirea să ajute și să nu încurce — «dar aceste snap-uri
 *  să fie doar la distanțe mici ca să am flexibilitate»:
 *
 *    1. RAZA E ÎN PIXELI, nu în metri. Un prag de 5 cm e o nimica toată la
 *       mărirea de ansamblu și un magnet care nu se lasă la mărirea de detaliu,
 *       adică exact pe dos decât trebuie: cine a mărit până vede centimetrul
 *       vrea să-l poată nimeri. `razaSnap` traduce pixelii în metri la mărirea
 *       de acum, cu un plafon ca la mărire foarte mică să nu înghită jumătate
 *       de cameră.
 *    2. NUMAI VECINII DIN JUR. O muchie se lipește de alta doar dacă cele două
 *       obiecte se și văd între ele pe cealaltă axă — se suprapun, sau au între
 *       ele mai puțin de `APROAPE`. Altfel un dulap din capătul celălalt al
 *       modulului ar trage de piesa de-aici, iar «aliniat» n-ar mai însemna
 *       nimic pe desen.
 *    3. NUMAI OBIECTELE DREPTE. O piesă rotită cu 37° n-are muchii pe axele
 *       planșei, deci n-are ce alinia: cutia ei de încadrare e o invenție a
 *       desenului, nu o muchie a mobilei. Rotită la un unghi drept, are — și
 *       atunci se lipește ca oricare alta.
 *
 *  LIPIREA DE UNGHI merge pe același tipar: candidații sunt unghiurile drepte
 *  ale planșei (0°, 90°, 180°, 270°) și cele ale obiectelor din jur, plus
 *  sferturile lor de tur — «să facă un snap către 90 de grade sau 180 de grade
 *  relativ de obiectele apropiate și chiar de plan». Tot cu o rază mică: patru
 *  grade, cât să prindă unghiul drept fără să interzică unul oblic. */

import type { PlanTronson, Pt } from "../doc";

/** Cutia unui obiect pe axele planșei: muchiile lui, per axă. */
export type Cutie = { x: [number, number]; y: [number, number] };

/** O muchie: linia pe care stă (axa + valoarea) și cât ține din cealaltă axă.
 *  Întinderea e și ea o măsură, nu un moft: din ea se vede dacă doi vecini se
 *  privesc între ei, și tot din ea se desenează linia de ghidaj. */
export type Muchie = {
  /** Axa pe care se măsoară `valoare`: 0 = X, 1 = Y. */
  axa: 0 | 1;
  valoare: number;
  /** Întinderea muchiei pe CEALALTĂ axă. */
  span: [number, number];
};

/** Ce a ieșit dintr-o lipire: deplasarea de adăugat și muchiile care s-au
 *  potrivit, ca desenul să poată arăta pe ce s-a lipit. */
export type Lipitura = {
  dep: Pt;
  /** Câte una pe axă, cel mult — linia vecinului, întinsă peste amândoi. */
  linii: Muchie[];
};

/** Cât de aproape trebuie să fie doi vecini pe cealaltă axă ca o muchie să se
 *  lipească de alta. Doi metri: cât o cameră obișnuită pe latura scurtă, deci
 *  «obiectele din jur» chiar sunt cele din jur, nu tot etajul. */
export const APROAPE = 2.0;

/** CÂT POATE RĂTĂCI O LATURĂ DE LA O AXĂ și să fie tot socotită dreaptă.
 *
 *  Era ZERO CURAT (`1e-6`), iar asta stingea lipirea pe planurile adevărate:
 *  un zid care a trecut prin mâna cuiva — sau prin transcriere — nu mai e
 *  perfect drept, iar o latură cu o abatere de un milimetru nu mai dădea
 *  NICIO muchie de lipit și nici nu se mai putea lipi ea de altceva. De-aia
 *  «uneori nu funcționează snapul la pereți» (26.10.2026): mergea pe catalogul
 *  perfect drept și tăcea pe planul de lucru.
 *
 *  CINCI MILIMETRI, măsurat: pe catalog, pe `m6-editat`, pe planul importat și
 *  pe documentul arhitectului, laturile neaxiale sunt 1–4,5 % și rătăcesc
 *  1–10 mm. Cinci prind zgomotul transcrierii (1–3 mm, cel mai des) și lasă
 *  afară ce e strâmb DINADINS — pe M5 e o latură cu 52 mm, adică un zid chiar
 *  oblic, care n-are ce căuta într-o aliniere pe axe.
 *
 *  Vezi și [[vecinul-usor-stramb]]: același fel de prag, pus din același motiv
 *  — pragurile desenului se măsoară în milimetri, nu în zerouri. */
export const ABATERE_DREAPTA = 0.005;

/** RAZA DE LIPIRE, în pixeli de ecran. Opt: cât greșește mâna la o tragere
 *  normală, și mai puțin decât ținta cea mai mică de pe planșă. */
export const SNAP_PX = 8;

/** Plafonul razei, în metri. La mărirea de ansamblu 8 px ar însemna 15–20 cm,
 *  adică un magnet care sare peste rosturi întregi. */
export const SNAP_MAX = 0.06;

/** Raza de lipire în metri, la mărirea de acum. `k` = pixeli pe metru. */
export function razaSnap(k: number): number {
  return Math.min(SNAP_MAX, SNAP_PX / Math.max(k, 1e-6));
}

/** Cât de departe de un unghi drept se mai lipește rotirea. Patru grade: cât
 *  să prindă dreptul, destul de puțin cât un unghi ales anume să rămână al
 *  omului. */
export const SNAP_UNGHI = 4;

/** Cât de departe se uită rotirea după unghiurile vecinilor. */
export const RAZA_UNGHI = 3.0;

const r3 = (v: number) => Math.round(v * 1000) / 1000;

export function cutiaPunctelor(pts: readonly Pt[]): Cutie {
  const xs = pts.map((q) => q[0]);
  const ys = pts.map((q) => q[1]);
  return { x: [Math.min(...xs), Math.max(...xs)],
           y: [Math.min(...ys), Math.max(...ys)] };
}

/** CUTIA UNEI PIESE DE MOBILIER — sau `null` dacă e rotită strâmb.
 *
 *  `null` nu e o scăpare, e răspunsul: o piesă oblică n-are muchii pe axele
 *  planșei (v. opreliștea 3 din antet), deci n-are cu ce se alinia. Cine
 *  primește `null` nu lipește nimic și lasă mâna în pace. */
export function cutiaPiesei(
  p: { c: Pt; w: number; h: number; deg: number },
): Cutie | null {
  const q = ((p.deg % 90) + 90) % 90;
  if (Math.min(q, 90 - q) > 0.5) return null;
  // La 90° și 270° piesa stă pe muchia cealaltă: lățimea și adâncimea se
  // schimbă între ele.
  const intors = Math.abs(((((p.deg % 180) + 180) % 180)) - 90) < 45;
  const w = intors ? p.h : p.w;
  const h = intors ? p.w : p.h;
  return { x: [p.c[0] - w / 2, p.c[0] + w / 2],
           y: [p.c[1] - h / 2, p.c[1] + h / 2] };
}

/** Cele patru muchii ale unei cutii. */
export function muchiileCutiei(c: Cutie): Muchie[] {
  return [
    { axa: 0, valoare: c.x[0], span: c.y },
    { axa: 0, valoare: c.x[1], span: c.y },
    { axa: 1, valoare: c.y[0], span: c.x },
    { axa: 1, valoare: c.y[1], span: c.x },
  ];
}

/** Muchiile drepte ale unui poligon: fiecare latură paralelă cu o axă a
 *  planșei. Pentru un dreptunghi ies chiar cele patru muchii ale lui; pentru un
 *  contur în L ies și cele dinăuntru, care sunt și ele muchii adevărate. */
function muchiilePoligonului(inel: readonly Pt[]): Muchie[] {
  const out: Muchie[] = [];
  for (let i = 0; i < inel.length; i++) {
    const a = inel[i];
    const b = inel[(i + 1) % inel.length];
    const dx = Math.abs(b[0] - a[0]);
    const dy = Math.abs(b[1] - a[1]);
    if (dx < 1e-6 && dy < 1e-6) continue;
    // PE MIJLOC, nu pe capăt: o latură care rătăcește câțiva milimetri n-are o
    // singură coordonată, iar luată de la un capăt muchia ar ieși cu toată
    // abaterea în ea. Mijlocul o înjumătățește.
    if (dx < ABATERE_DREAPTA && dx <= dy) {
      out.push({ axa: 0, valoare: (a[0] + b[0]) / 2,
                 span: [Math.min(a[1], b[1]), Math.max(a[1], b[1])] });
    } else if (dy < ABATERE_DREAPTA && dy <= dx) {
      out.push({ axa: 1, valoare: (a[1] + b[1]) / 2,
                 span: [Math.min(a[0], b[0]), Math.max(a[0], b[0])] });
    }
  }
  return out;
}

/** Ce se scoate din căutare: obiectul care se mută nu se poate alinia la el
 *  însuși. */
export type Fara = {
  piesa?: number; parte?: number;
  /** Zidurile care pleacă odată cu cel tras — el și foile lipite de el. */
  pereti?: ReadonlySet<number>;
  /** Un ZID nu se lipește de mobilă: mobila se așază după ziduri, nu invers. */
  faraPiese?: boolean;
};

/** MUCHIILE DIN JUR, de pe toată planșa.
 *
 *  Pereții intră cu INELUL lor, nu cu axul: se aliniază fața, adică ce se vede
 *  pe planșă și ce se tencuiește — aceeași regulă ca la lipirea zidurilor între
 *  ele. Golurile nu intră: un tocul de ușă nu e o muchie de care se sprijină
 *  ceva, iar o canapea lipită de el n-ar însemna nimic. */
export function muchiileDinJur(plan: PlanTronson, fara: Fara = {}): Muchie[] {
  const out: Muchie[] = [];
  plan.pereti.forEach((w, i) => {
    if (w.bulge || fara.pereti?.has(i)) return;    // arc: n-are muchie dreaptă
    out.push(...muchiilePoligonului(w.inel));
  });
  plan.piese.forEach((p, i) => {
    if (fara.faraPiese || i === fara.piesa) return;
    const c = cutiaPiesei(p);
    if (c) out.push(...muchiileCutiei(c));
  });
  if (plan.nucleu) {
    const [x0, y0, x1, y1] = plan.nucleu.rect;
    out.push(...muchiileCutiei({ x: [Math.min(x0, x1), Math.max(x0, x1)],
                                 y: [Math.min(y0, y1), Math.max(y0, y1)] }));
    plan.nucleu.parti.forEach((p, i) => {
      if (i === fara.parte) return;
      out.push(...muchiilePoligonului(p.inel));
    });
  }
  out.push(...muchiilePoligonului(plan.anvelopa));
  return out;
}

/** Se văd cele două muchii între ele pe cealaltă axă? Suprapuse, sau cu mai
 *  puțin de `APROAPE` între ele. */
function seVad(a: readonly [number, number], b: readonly [number, number]): boolean {
  const gol = Math.max(a[0], b[0]) - Math.min(a[1], b[1]);
  return gol <= APROAPE;
}

/** LIPEȘTE O SINGURĂ MUCHIE de cea mai apropiată muchie de vecin.
 *
 *  Întoarce deplasarea de adăugat valorii și linia pe care s-a lipit, sau
 *  `null` când nu e nimic destul de aproape. Asta e cărămida din care se fac
 *  și mutarea (patru muchii deodată), și redimensionarea (numai muchiile
 *  trase). */
export function lipesteMuchia(
  m: Muchie, vecine: readonly Muchie[], tol: number,
): { delta: number; linie: Muchie } | null {
  let cel: { delta: number; linie: Muchie; d: number } | null = null;
  for (const v of vecine) {
    if (v.axa !== m.axa) continue;
    const d = Math.abs(v.valoare - m.valoare);
    if (d > tol) continue;
    if (!seVad(m.span, v.span)) continue;
    if (!cel || d < cel.d) cel = { delta: v.valoare - m.valoare, linie: v, d };
  }
  return cel ? { delta: cel.delta, linie: cel.linie } : null;
}

/** LIPEȘTE O CUTIE ÎNTREAGĂ — gestul de mutare.
 *
 *  Pe fiecare axă se încearcă amândouă muchiile cutiei și se ia potrivirea cea
 *  mai strânsă: o piesă adusă lângă un perete se lipește de el cu fața dinspre
 *  el, nu cu cea dinapoi, fiindcă aia e mai aproape. Axele se hotărăsc una câte
 *  una, deci o piesă se poate lipi pe X fără să fie trasă pe Y. */
export function lipesteCutia(
  c: Cutie, vecine: readonly Muchie[], tol: number,
): Lipitura {
  const dep: Pt = [0, 0];
  const linii: Muchie[] = [];
  for (const axa of [0, 1] as const) {
    let cel: { delta: number; linie: Muchie; d: number } | null = null;
    for (const m of muchiileCutiei(c)) {
      if (m.axa !== axa) continue;
      const l = lipesteMuchia(m, vecine, tol);
      if (!l) continue;
      const d = Math.abs(l.delta);
      if (!cel || d < cel.d) cel = { ...l, d };
    }
    if (cel) {
      dep[axa] = r3(cel.delta);
      linii.push(cel.linie);
    }
  }
  return { dep, linii };
}

/** How near a wall face a stair snaps, in metres, when it is placed or
 *  dragged. 15 cm: a stair lives in a stairwell, against walls, and the 6 cm
 *  of furniture (`SNAP_MAX`) is three pixels at the working zoom, which Tudor
 *  never felt (28.10.2026). */
export const LIPIRE_SCARA = 0.15;

/** A wall face near a press: the line it is on and the side the hand is on
 *  (`parte`: +1 when the hand's coordinate on `axa` is above the face's). */
export type FataPornirii = { axa: 0 | 1; valoare: number; parte: 1 | -1 };

/** THE WALL FACES A STAIR CAN START AGAINST, around the press `q`: the
 *  nearest face within `raza` on each axis, so a corner gives two. Asked by
 *  Tudor (28.10.2026): "starting the stair in a point, ideally snapped to a
 *  wall, and then dragging it to set its length". */
export function fetelePornirii(
  vecine: readonly Muchie[], q: Pt, raza = LIPIRE_SCARA,
): FataPornirii[] {
  const fete: (FataPornirii & { d: number })[] = [];
  for (const m of vecine) {
    const d = Math.abs(q[m.axa] - m.valoare);
    const t = q[1 - m.axa];
    if (d > raza || t < m.span[0] - raza || t > m.span[1] + raza) continue;
    const f = { axa: m.axa, valoare: m.valoare, d,
                parte: (q[m.axa] - m.valoare < 0 ? -1 : 1) as 1 | -1 };
    const i = fete.findIndex((x) => x.axa === m.axa);
    if (i < 0) fete.push(f);
    else if (d < fete[i].d) fete[i] = f;
  }
  return fete.map(({ d: _d, ...f }) => f);
}

/** THE WAY A STAIR CLIMBS from the hand's pull `v` and the faces beside the
 *  press: the nearer axis, and never into a face. Pulled into a wall, the
 *  flight turns along it, the way the hand leans. `liber` (Shift) keeps the
 *  hand's own angle. Before any pull, along the first face, or +x. */
export function directiaScarii(
  fete: readonly FataPornirii[], v: Pt | null, liber = false,
): Pt {
  if (!v || (!v[0] && !v[1])) {
    const f = fete[0];
    return f ? (f.axa === 0 ? [0, 1] : [1, 0]) : [1, 0];
  }
  const L = Math.hypot(v[0], v[1]);
  if (liber) return [v[0] / L, v[1] / L];
  let dir: Pt = Math.abs(v[0]) >= Math.abs(v[1])
    ? [Math.sign(v[0]), 0] : [0, Math.sign(v[1])];
  const inZid = fete.find((f) => dir[f.axa] === -f.parte);
  if (inZid) {
    const b = 1 - inZid.axa;
    const s = Math.sign(v[b]) || 1;
    dir = b === 0 ? [s, 0] : [0, s];
  }
  return dir;
}

/** WHERE THE STAIR STARTS for the way `dir`, against the faces beside the
 *  press: a face the flight runs along takes the flight's EDGE, half a width
 *  in on the hand's side; a face the flight climbs away from takes its
 *  START. In a corner both apply. `spreZid` points from the flight to the
 *  face it runs along, so a U can open the other way; null without one.
 *
 *  The first version put every flight along the nearest face, so a stair
 *  pressed by a horizontal wall could not climb away from it, and a vertical
 *  pull counted nothing (tested 28.10.2026, "clip it to horizontal walls and
 *  draw it facing up"). */
export function pornireaScarii(
  fete: readonly FataPornirii[], q: Pt, dir: Pt, latime: number,
): { de: Pt; spreZid: Pt | null } {
  const de: Pt = [q[0], q[1]];
  let spreZid: Pt | null = null;
  for (const f of fete) {
    if (Math.abs(dir[f.axa]) < 0.5) {
      de[f.axa] = r3(f.valoare + f.parte * latime / 2);
      spreZid = f.axa === 0 ? [-f.parte, 0] : [0, -f.parte];
    } else {
      de[f.axa] = r3(f.valoare);
    }
  }
  return { de, spreZid };
}

/** UNGHIURILE OBIECTELOR DIN JURUL UNUI PUNCT, în grade.
 *
 *  Piesele își spun unghiul de-a dreptul; pereții îl au în ax. Unghiurile se
 *  strâng în [0, 90): oricum se caută sferturi de tur, iar un perete e la fel
 *  de «drept» față de tine la 0° și la 180°. */
export function unghiurileDinJur(
  plan: PlanTronson, c: Pt, fara: Fara = {}, raza = RAZA_UNGHI,
): number[] {
  const out: number[] = [];
  const aproape = (p: Pt) => Math.hypot(p[0] - c[0], p[1] - c[1]) <= raza;
  plan.piese.forEach((p, i) => {
    if (i === fara.piesa || !aproape(p.c)) return;
    out.push(((p.deg % 90) + 90) % 90);
  });
  for (const w of plan.pereti) {
    if (w.bulge || !w.inel.length) continue;
    const mij: Pt = [
      w.inel.reduce((s, q) => s + q[0], 0) / w.inel.length,
      w.inel.reduce((s, q) => s + q[1], 0) / w.inel.length,
    ];
    if (!aproape(mij)) continue;
    const ax = w.ax ?? [w.inel[0], w.inel[1]];
    const u = (Math.atan2(ax[1][1] - ax[0][1], ax[1][0] - ax[0][0]) * 180) / Math.PI;
    out.push(((u % 90) + 90) % 90);
  }
  return out;
}

/** LIPEȘTE UN UNGHI de cel mai apropiat unghi drept.
 *
 *  Candidații: sferturile de tur ale planșei (0, 90, 180, 270) și sferturile
 *  de tur ale fiecărui obiect din jur. Unghiul planșei intră ÎNTOTDEAUNA în
 *  listă — el e cel pe care îl caută mâna când nu are alt reper —, iar cel al
 *  vecinilor peste el: o canapea pusă lângă un perete oblic se întoarce pe
 *  peretele ăla, nu pe nordul desenului. */
export function lipesteUnghiul(
  unghi: number, vecine: readonly number[], tol = SNAP_UNGHI,
): number {
  const norm = (v: number) => ((v % 360) + 360) % 360;
  const u = norm(unghi);
  let cel: { v: number; d: number } | null = null;
  // 45: the sheet's diagonals, for the 45° corner modules (OB-42).
  for (const baza of [0, 45, ...vecine]) {
    for (let s = 0; s < 4; s++) {
      const t = norm(baza + s * 90);
      const dd = Math.abs(u - t);
      const d = Math.min(dd, 360 - dd);
      if (d > tol) continue;
      if (!cel || d < cel.d) cel = { v: t, d };
    }
  }
  return cel ? cel.v : norm(unghi);
}

/** LIPEȘTE UN ZID TRAS DE CORP — muchiile lui de muchiile zidurilor din jur,
 *  pe AMÂNDOUĂ axele.
 *
 *  Până aici zidul avea lipirea lui (`model::lipirea`), și de acolo veneau
 *  amândouă plângerile — «face snap în locurile greșite» și «e imposibil să-i
 *  aliniez de mână»:
 *
 *    * se uita la fețele paralele de pe TOT etajul, fără să întrebe dacă
 *      zidul ăla e prin preajmă: un perete din capătul celălalt al modulului,
 *      cu fața la 2 cm de linia pe care treceai, te trăgea pe ea;
 *    * pe LUNGUL zidului nu se lipea nimic, deci capetele cădeau doar din 5 în
 *      5 cm de unde plecaseră. Un parapet de 15 cm n-ajungea niciodată cu
 *      capătul pe fața zidului din colț — rămânea la 2,5 cm, dincolo sau
 *      dincoace.
 *
 *  Acum zidul e o cutie ca oricare alta, cu aceleași trei oprelițe ca mobila
 *  (v. antetul): muchie cu muchie, numai vecinii din jur, raza în pixeli. Pe
 *  fiecare axă câștigă potrivirea cea mai strânsă, iar o axă pe care nu s-a
 *  lipit nimic merge pe pasul `pas`, cum mergea. Ce iese pe planșă sunt exact
 *  cele două colțuri închise pe care le cere mâna: lipit de fața vecinului sau
 *  aliniat cu capătul lui.
 *
 *  `grup` = zidul și foile lipite de el (v. `model::lipiteleLui`): pleacă
 *  împreună, deci nu se lipesc între ele, iar cutia e a sandvișului întreg.
 *  Mobila nu intră — zidul nu se așază după un dulap.
 *
 *  `null` când vreun zid din grup nu e drept pe axele planșei (oblic sau
 *  curb): n-are muchii de lipit, iar cine îl primește merge pe pas. */
export function lipesteZidul(
  plan: PlanTronson, grup: ReadonlySet<number>, brut: Pt, tol: number,
  pas: number,
): Lipitura | null {
  const pts: Pt[] = [];
  for (const i of grup) {
    const w = plan.pereti[i];
    if (!w || w.bulge) return null;
    // DREPT CU TOLERANȚĂ, din același motiv: cerut perfect drept, un singur
    // zid ușor strâmb din grup stingea lipirea pentru tot gestul.
    const drept = w.inel.every((a, k) => {
      const b = w.inel[(k + 1) % w.inel.length];
      return Math.abs(b[0] - a[0]) < ABATERE_DREAPTA
        || Math.abs(b[1] - a[1]) < ABATERE_DREAPTA;
    });
    if (!drept) return null;
    pts.push(...w.inel);
  }
  if (!pts.length) return null;
  const c0 = cutiaPunctelor(pts);
  const mutata: Cutie = { x: [c0.x[0] + brut[0], c0.x[1] + brut[0]],
                          y: [c0.y[0] + brut[1], c0.y[1] + brut[1]] };
  const l = lipesteCutia(mutata, muchiileDinJur(plan, { pereti: grup, faraPiese: true }),
                         tol);
  const pePas = (v: number) => r3(Math.round(v / pas) * pas);
  const dep: Pt = [0, 1].map((axa) => (l.linii.some((m) => m.axa === axa)
    ? r3(brut[axa] + l.dep[axa]) : pePas(brut[axa]))) as Pt;
  return { dep, linii: l.linii };
}

/** A PIECE TURNED TO 45° SLIDES FLUSH ONTO A PARALLEL DIAGONAL WALL FACE.
 *
 *  The axis snaps above know only edges along the plan's axes (prohibition 3),
 *  so on a 45° corner module a cabinet turned onto the diagonal never met its
 *  wall. This is the same edge-to-edge rule along a diagonal: the piece's
 *  nearest parallel edge onto the face, from the room side of the face only,
 *  within `tol`, when the two overlap along the face. Returns the displacement,
 *  or `null`. */
export function lipestePiesaOblica(
  p: { c: Pt; w: number; h: number; deg: number },
  pereti: readonly { inel: readonly Pt[]; bulge?: number }[],
  tol: number,
): Pt | null {
  const q = ((p.deg % 90) + 90) % 90;
  if (Math.abs(q - 45) > 0.5) return null;
  const t = (p.deg * Math.PI) / 180;
  const e1: Pt = [Math.cos(t), Math.sin(t)];
  const e2: Pt = [-Math.sin(t), Math.cos(t)];
  const PARALEL = Math.sin((0.5 * Math.PI) / 180);
  let cel: { dep: Pt; d: number } | null = null;
  for (const w of pereti) {
    if (w.bulge) continue;
    const P = w.inel;
    if (P.length < 3) continue;
    const g: Pt = [P.reduce((s, v) => s + v[0], 0) / P.length,
                   P.reduce((s, v) => s + v[1], 0) / P.length];
    for (let k = 0; k < P.length; k++) {
      const a = P[k], b = P[(k + 1) % P.length];
      const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
      if (L < 1e-6) continue;
      const d: Pt = [(b[0] - a[0]) / L, (b[1] - a[1]) / L];
      if (Math.abs(d[0] * e1[1] - d[1] * e1[0]) > PARALEL
          && Math.abs(d[0] * e2[1] - d[1] * e2[0]) > PARALEL) continue;
      // The face's outward normal: away from the wall's body.
      let n: Pt = [-d[1], d[0]];
      if ((g[0] - a[0]) * n[0] + (g[1] - a[1]) * n[1] > 0) n = [-n[0], -n[1]];
      const fata = (p.c[0] - a[0]) * n[0] + (p.c[1] - a[1]) * n[1];
      if (fata <= 0) continue;
      const hn = Math.abs((p.w / 2) * (e1[0] * n[0] + e1[1] * n[1]))
        + Math.abs((p.h / 2) * (e2[0] * n[0] + e2[1] * n[1]));
      const gol = fata - hn;
      if (Math.abs(gol) > tol) continue;
      const tc = (p.c[0] - a[0]) * d[0] + (p.c[1] - a[1]) * d[1];
      const hd = Math.abs((p.w / 2) * (e1[0] * d[0] + e1[1] * d[1]))
        + Math.abs((p.h / 2) * (e2[0] * d[0] + e2[1] * d[1]));
      if (tc + hd <= 0 || tc - hd >= L) continue;
      if (!cel || Math.abs(gol) < cel.d) cel = { dep: [-gol * n[0], -gol * n[1]], d: Math.abs(gol) };
    }
  }
  // Not rounded to the millimetre: on a diagonal that would leave the piece
  // off the face by up to 0.7 mm.
  return cel ? cel.dep : null;
}
